import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getFullUrl } from '../config/apiConfig';
import logger from '../utils/logger';
import RichTextEditor from '../components/RichTextEditor';

import { userPool } from '../services/cognitoService';
import { 
  Clock, 
  ChevronLeft, 
  ChevronRight,
  Play,
  BookOpen,
  ChevronsLeft,
  ChevronsRight,
  X,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Maximize2,
  Minimize2,
  ChevronUp,
  ChevronDown
} from 'lucide-react';

const ExamParticipation = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshToken, user } = useAuth();
  
  const [examData, setExamData] = useState(null);
  const [examQuestions, setExamQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [examStarted, setExamStarted] = useState(false);
  const [questionsLoaded, setQuestionsLoaded] = useState(false);
  const [examSubmitted, setExamSubmitted] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Image carousel and modal states
  const [selectedImage, setSelectedImage] = useState(null);
  const [carouselPage, setCarouselPage] = useState(0);
  const imagesPerPage = 4;

  // Zoom and pan states for modal
  const [zoomScale, setZoomScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const fullscreenRef = useRef(null);

  useEffect(() => {
    const locationState = location.state;
    if (locationState && locationState.examData) {
      setExamData(locationState.examData);
      setTimeRemaining((locationState.examData.duration || 40) * 60);
      setLoading(false);
    } else {
      setError('No exam data provided. Please go back and try again.');
      setLoading(false);
    }
  }, [id, location]);

  const handleStartExam = async () => {
    try {
      setExamStarted(true);

      // Get token from backend session
      let idToken = null;
      let currentIdToken = null;
       const cognitoUser = userPool.getCurrentUser();
      if (cognitoUser) {
        currentIdToken = await new Promise((resolve, reject) => {
          cognitoUser.getSession((err, session) => {
            if (err) {
              reject(err);
              return;
            }
            if (session && session.isValid()) {
              resolve(session.getIdToken().getJwtToken());
            } else {
              reject(new Error('Invalid session'));
            }
          });
        });
      }
      try {
        const sessionResponse = await fetch(getFullUrl('auth/session'), {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            idToken: currentIdToken
          })
        });

        if (sessionResponse.ok) {
          const sessionData = await sessionResponse.json();
          idToken = sessionData.idToken;
        }
      } catch (error) {
        logger.error('Error getting session:', error);
      }
 

      const response = await fetch(getFullUrl('exam-questions'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user?.session?.idToken.jwtToken}`
        },
        body: JSON.stringify({
          purchasedPackage: examData.purchasedPackage,
          examId: examData.id
        })
      });

      if (!response.ok) {
        setError('Failed to load questions. Please try again.');
        setExamStarted(false);
        return;
      }
      
      const data = await response.json();

      let questions = [];
      
      if (data.body && typeof data.body === 'string') {
        questions = JSON.parse(data.body);
      } else if (data.questions && Array.isArray(data.questions)) {
        questions = data.questions;
      } else if (Array.isArray(data)) {
        questions = data;
      } else if (data.data && Array.isArray(data.data)) {
        questions = data.data;
      } else {
        setError('Failed to load questions. Invalid response format.');
        return;
      }

      const formattedQuestions = questions.map((q, index) => ({
        questionId: q.questionId || `q${index + 1}`,
        question: q.question || q.questionText,
        options: q.options || q.choices || [],
        correctAnswer: q.correctAnswer || q.answer,
        correctAnswers: q.correctAnswers || q.correctAnswer, // Handle both singular and plural
        passage: q.passage || q.comprehension,
        image: q.questionImage || q.image || q.imageUrl, // Fix field order
        questionType: q.questionType,
        explanations: q.explanations && typeof q.explanations === 'string' 
          ? JSON.parse(q.explanations) 
          : q.explanations || q.explanation, // Handle both plural and singular
        answerImages: q.answerImages || [] // Add answer images array
      }));

      setExamQuestions(formattedQuestions);
      setQuestionsLoaded(true);
    } catch (err) {
      
      // Handle 401 Unauthorized error by refreshing token
      if (err.message && err.message.includes('401')) {
        try {
          await refreshToken();
          
          // Retry the request after token refresh
          const retryResponse = await fetch(getFullUrl('exam-questions'), {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              purchasedPackage: examData.purchasedPackage,
              examId: examData.id
            })
          });
          
          if (!retryResponse.ok) {
            throw new Error(`HTTP error! status: ${retryResponse.status}`);
          }
          
          const retryData = await retryResponse.json();

          let questions = [];
          
          if (retryData.body && typeof retryData.body === 'string') {
            questions = JSON.parse(retryData.body);
          } else if (retryData.questions && Array.isArray(retryData.questions)) {
            questions = retryData.questions;
          } else if (Array.isArray(retryData)) {
            questions = retryData;
          } else if (retryData.data && Array.isArray(retryData.data)) {
            questions = retryData.data;
          } else {
            setError('Failed to load questions. Please try again.');
            return;
          }

          const formattedQuestions = questions.map((q, index) => ({
            questionId: q.questionId || `q${index + 1}`,
            question: q.question || q.questionText,
            options: q.options || q.choices || [],
            correctAnswer: q.correctAnswer || q.answer,
            correctAnswers: q.correctAnswers || q.correctAnswer, // Handle both singular and plural
            passage: q.passage || q.comprehension,
            image: q.image || q.imageUrl,
            questionType: q.questionType,
            explanations: q.explanations && typeof q.explanations === 'string' 
              ? JSON.parse(q.explanations) 
              : q.explanations || q.explanation // Handle both plural and singular
          }));

          setExamQuestions(formattedQuestions);
          setQuestionsLoaded(true);
          
        } catch (refreshError) {
          setError('Session expired. Please log in again.');
          // Redirect to login after a short delay
          setTimeout(() => {
            navigate('/login');
          }, 2000);
        }
      } else {
        setError('Failed to load questions. Please try again.');
      }
    }
  };

  const handleAnswerSelect = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < examQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const currentQuestion = examQuestions[currentQuestionIndex] || null;
  
 

  const submitExam = useCallback(() => {
    setExamSubmitted(true);
    let correctAnswers = 0;
    examQuestions.forEach(question => {
      const userAnswer = answers[question.questionId];
      const correctAnswer = question.correctAnswers || question.correctAnswer;
      
      // Convert correctAnswer to array, handling comma-separated indices like "1,2"
      let correctAnswersArray;
      if (Array.isArray(correctAnswer)) {
        correctAnswersArray = correctAnswer;
      } else if (typeof correctAnswer === 'string' && correctAnswer.includes(',')) {
        correctAnswersArray = correctAnswer.split(',');
      } else {
        correctAnswersArray = [correctAnswer];
      }
      
      let isCorrect = false;
      if (question.questionType === 'LONG_WRITING') {
        // For long writing, always consider correct if any answer provided
        isCorrect = userAnswer && userAnswer.trim().length > 0;
      } else if (question.questionType === 'MULTI_SELECT') {
        // For multi-select, check if arrays match (convert indices to letters)
        const userAnswersArray = Array.isArray(userAnswer) ? userAnswer : [];

        const correctAnswersAsLetters = correctAnswersArray.map(ans => {
          const index = parseInt(ans);
          return isNaN(index) ? ans : String.fromCharCode(65 + index);
        });
        isCorrect = userAnswersArray.length === correctAnswersAsLetters.length && 
                   userAnswersArray.every(ans => correctAnswersAsLetters.includes(ans));
      } else if (question.questionType === 'SHORT_ANSWER') {
        // For short answer, compare case-insensitive with type conversion
        const userStr = String(userAnswer).trim().toLowerCase();
        const correctStr = String(correctAnswer).trim().toLowerCase();
        isCorrect = userStr === correctStr || Number(userAnswer) === Number(correctAnswer);
      } else {
        // For MCQ, compare selected option
        if (userAnswer !== undefined && userAnswer !== null) {
          const selectedOption = question.options[userAnswer];
          const selectedOptionId = selectedOption?.id || '';
          const correctIndex = parseInt(correctAnswer);
          isCorrect = selectedOptionId === correctAnswer || 
                      selectedOption === correctAnswer ||
                      (typeof correctAnswer === 'string' && selectedOption?.text === correctAnswer) ||
                      (!isNaN(correctIndex) && Number(userAnswer) === correctIndex);
        }
      }
      
      if (isCorrect) {
        correctAnswers++;
      }
    });
    
    const score = Math.round((correctAnswers / examQuestions.length) * 100);
    const timeTaken = (examData.duration * 60) - timeRemaining;
    
    navigate(`/exam/results/${examData.id}`, {
      state: { 
        examData: { ...examData, id: examData.id }, 
        answers, 
        score, 
        totalQuestions: examQuestions.length, 
        correctAnswers, 
        timeTaken, 
        questions: examQuestions 
      }
    });
  }, [answers, examQuestions, examData, id, navigate, timeRemaining]);

  // Timer useEffect
  useEffect(() => {
    if (examStarted && !examSubmitted && timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            // Auto-submit when time runs out
            submitExam();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [examStarted, examSubmitted, timeRemaining, submitExam]);

  const confirmSubmitExam = () => {
    setShowConfirmDialog(true);
  };

  const cancelSubmitExam = () => {
    setShowConfirmDialog(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimeColor = () => {
    if (timeRemaining < 300) return 'text-red-600';
    if (timeRemaining < 600) return 'text-yellow-600';
    return 'text-green-600';
  };

  // Sync isFullscreen state with browser fullscreen events
  // Must be declared before any early returns to satisfy Rules of Hooks
  useEffect(() => {
    const onFsChange = () => {
      const isFull = !!(document.fullscreenElement || document.webkitFullscreenElement);
      setIsFullscreen(isFull);
      // If user pressed Escape to exit fullscreen, close the viewer too
      if (!isFull) {
        setSelectedImage(null);
        setZoomScale(1);
        setPan({ x: 0, y: 0 });
      }
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
    };
  }, []);

  if (loading) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  if (error) {
    return <div className="p-8 text-center text-red-600">{error}</div>;
  }

  if (!examStarted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-8">
        <div className="max-w-4xl w-full mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                <BookOpen className="h-8 w-8 text-blue-600" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Ready to Start Exam</h1>
              <p className="text-gray-600">Good luck! Take your time and answer carefully.</p>
            </div>
            
            <div className="space-y-4">
              <div className="border-l-4 border-gray-200 pl-4">
                {/* <div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">Exam Details</h3>
                  <div className="space-y-2">
                    <div>
                      <span className="font-medium">Title:</span> {examData?.title || 'Loading...'}
                    </div>
                    <div>
                      <span className="font-medium">Duration:</span> {examData?.duration || 40} minutes
                    </div>
                    <div>
                      <span className="font-medium">Questions:</span> {examQuestions.length || 0}
                    </div>
                  </div>
                </div> */}
              </div>
            </div>
            
            <div className="text-center mt-8">
              <div className="inline-flex items-center justify-center space-x-2 text-sm text-gray-500 mb-16">
                <Clock className="h-4 w-4" />
                <span>{examData?.duration || 40} minutes</span>
              </div>
              <div className="border-t border-gray-200 pt-8">
                <button 
                  onClick={handleStartExam} 
                  className="btn-primary inline-flex items-center space-x-2 px-8 py-3 text-lg font-medium"
                >
                  <Play className="h-5 w-5" />
                  <span>Start Exam</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!questionsLoaded) {
    return <div className="p-8 text-center">Starting exam...</div>;
  }

  // Get all question images for the carousel
  const getAllQuestionImages = () => {
    return examQuestions
      .map((q, index) => ({
        questionIndex: index,
        questionNumber: index + 1,
        image: q.image,
        hasImage: !!q.image
      }))
      .filter(item => item.hasImage);
  };

  const allQuestionImages = getAllQuestionImages();
  const totalCarouselPages = Math.ceil(allQuestionImages.length / imagesPerPage);
  const currentCarouselImages = allQuestionImages.slice(
    carouselPage * imagesPerPage,
    (carouselPage + 1) * imagesPerPage
  );

  const handleCarouselPrev = () => {
    setCarouselPage(prev => Math.max(0, prev - 1));
  };

  const handleCarouselNext = () => {
    setCarouselPage(prev => Math.min(totalCarouselPages - 1, prev + 1));
  };

  const handleImageClick = (image) => {
    setSelectedImage(image);
    setZoomScale(1);
    setPan({ x: 0, y: 0 });
  };

  const closeModal = () => {
    setSelectedImage(null);
    setZoomScale(1);
    setPan({ x: 0, y: 0 });
    setIsFullscreen(false);
  };

  const handleMouseDown = (e) => {
    if (zoomScale > 1) {
      e.preventDefault(); // Prevent browser native image dragging
      setIsDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoomScale > 1) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    if (zoomScale > 1 && e.touches.length === 1) {
      e.preventDefault(); // Prevent touch scrolling when zoomed
      setIsDragging(true);
      setDragStart({ x: e.touches[0].clientX - pan.x, y: e.touches[0].clientY - pan.y });
    }
  };

  const handleTouchMove = (e) => {
    if (isDragging && zoomScale > 1 && e.touches.length === 1) {
      setPan({
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y
      });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const handleDoubleClick = () => {
    if (zoomScale > 1) {
      setZoomScale(1);
      setPan({ x: 0, y: 0 });
    } else {
      setZoomScale(2);
      setPan({ x: 0, y: 0 });
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left Column - Main Content */}
        <div className="flex-1 order-2 lg:order-1">
          <div className="bg-white rounded-lg shadow mb-6">
        <div className="px-6 py-4 border-b">
          <div className="flex flex-col sm:flex-row sm:justify-between gap-2">
            <h1 className="text-lg sm:text-xl font-semibold">{examData.title}</h1>
            <div className="flex items-center space-x-4">
              <Clock className={`h-5 w-5 ${getTimeColor()}`} />
              <span className={`font-medium ${getTimeColor()}`}>
                {formatTime(timeRemaining)}
              </span>
            </div>
          </div>
        </div>
        <div className="px-6 py-3">
          <div className="bg-gray-200 h-2 rounded">
            <div
              className="bg-blue-600 h-2 rounded"
              style={{
                width: examQuestions.length
                  ? `${((currentQuestionIndex + 1) / examQuestions.length) * 100}%`
                  : '0%'
              }}
            />
          </div>
        </div>
      </div>

          {/* Question Content */}
          {currentQuestion && (
            <div className="px-6 py-4">
              <div className="mb-6">
            {/* Question Type: SHORT_ANSWER */}
            {currentQuestion.questionType === 'SHORT_ANSWER' && (
              <div className="bg-yellow-50 p-4 rounded-lg mb-4">
                <h3 className="font-semibold text-lg mb-2">Short Answer</h3>
                <textarea
                  className="w-full p-2 border rounded-lg text-sm"
                  rows={2}
                  placeholder="Type your answer here..."
                  value={answers[currentQuestion.questionId] || ''}
                  onChange={(e) => handleAnswerSelect(currentQuestion.questionId, e.target.value)}
                />
              </div>
            )}

            {/* Question Type: LONG_WRITING */}
            {currentQuestion.questionType === 'LONG_WRITING' && (
              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                <h3 className="font-semibold text-lg mb-2">Long Writing Answer</h3>
                <RichTextEditor
                  value={answers[currentQuestion.questionId] || ''}
                  onChange={(value) => handleAnswerSelect(currentQuestion.questionId, value)}
                  rows={10}
                  placeholder="Write your detailed answer here..."
                  maxWords={900}
                />
                <div className="mt-2 text-sm text-gray-600 text-right">
                  {(() => {
                    const answer = answers[currentQuestion.questionId] || '';
                    // Strip HTML tags using regex, normalize whitespace, then count words
                    const plainText = answer.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
                    const wordCount = plainText.split(' ').filter(word => word.length > 0).length;
                    return (
                      <span className={wordCount > 900 ? 'text-red-600 font-medium' : ''}>
                        {wordCount} / 900 words
                      </span>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Question Text - Show first for all question types */}
            <h2 
              className="text-xl font-semibold mb-4 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: currentQuestion.question }}
            />

            {/* Question Image */}
            {currentQuestion.image && (
              <div className="mb-4 relative group cursor-zoom-in inline-block max-w-full">
                <img 
                  src={currentQuestion.image} 
                  alt="Question" 
                  className="max-w-full h-auto rounded-lg border border-gray-200 transition-all hover:opacity-95"
                  onClick={() => handleImageClick({
                    questionIndex: currentQuestionIndex,
                    questionNumber: currentQuestionIndex + 1,
                    image: currentQuestion.image
                  })}
                />
                <div 
                  className="absolute bottom-2 right-2 bg-black/60 hover:bg-black/85 text-white p-2 rounded-full cursor-pointer transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleImageClick({
                      questionIndex: currentQuestionIndex,
                      questionNumber: currentQuestionIndex + 1,
                      image: currentQuestion.image
                    });
                  }}
                >
                  <ZoomIn className="h-4 w-4" />
                </div>
              </div>
            )}

            {/* Question Type: MULTI_SELECT */}
            {currentQuestion.questionType === 'MULTI_SELECT' && (
              <div className="bg-purple-50 p-4 rounded-lg mb-4">
                <h3 className="font-semibold text-lg mb-2">Select All That Apply</h3>
                <div className="space-y-2">
                  {currentQuestion.options?.map((option, index) => {
                    const optionText = option.text || (typeof option === 'string' ? option : '');
                    const optionImage = currentQuestion.answerImages && currentQuestion.answerImages[index] ? currentQuestion.answerImages[index] : null;
                    return (
                      <label key={index} className="flex items-start space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 mt-1"
                          checked={answers[currentQuestion.questionId]?.includes(option.id) || false}
                          onChange={(e) => {
                            const currentAnswers = answers[currentQuestion.questionId] || [];
                            if (e.target.checked) {
                              handleAnswerSelect(currentQuestion.questionId, [...currentAnswers, option.id]);
                            } else {
                              handleAnswerSelect(currentQuestion.questionId, currentAnswers.filter(id => id !== option.id));
                            }
                          }}
                        />
                        <div className="flex-1">
                          {optionImage ? (
                            <img
                              src={optionImage}
                              alt={`Option ${String.fromCharCode(65 + index)}`}
                              className="max-w-full h-auto rounded-lg max-h-32 object-contain"
                            />
                          ) : (
                            <span className="text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: optionText }} />
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

          </div>
        </div>
      )}

      {/* Options - Only for MCQ questions */}
      {currentQuestion && currentQuestion.questionType === 'MCQ' && Array.isArray(currentQuestion.options) && currentQuestion.options.map((opt, i) => {
        const optionText = opt.text || (typeof opt === 'string' ? opt : '');
        const optionImage = currentQuestion.answerImages && currentQuestion.answerImages[i] ? currentQuestion.answerImages[i] : null;
        const isSelected = answers[currentQuestion.questionId] === i;
        const letterPrefix = String.fromCharCode(65 + i);
        
        return (
          <button
            key={i}
            onClick={() => handleAnswerSelect(currentQuestion.questionId, i)}
            className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-200 ${
              isSelected
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center">
              <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center mr-3 text-sm font-medium ${
                isSelected
                  ? 'border-blue-500 bg-blue-500 text-white'
                  : 'border-gray-300 text-gray-600'
              }`}>
                {letterPrefix}
              </div>
              <div className="flex-1">
                {optionImage ? (
                  <img 
                    src={optionImage} 
                    alt={`Option ${letterPrefix}`}
                    className="max-w-full h-auto rounded-lg max-h-32 object-contain"
                  />
                ) : (
                  <span className="leading-relaxed" dangerouslySetInnerHTML={{ __html: optionText }} />
                )}
              </div>
            </div>
          </button>
        );
      })}

          {/* Navigation */}
          {currentQuestion && (
        <div className="flex flex-col sm:flex-row items-center justify-between mt-6 px-6 gap-4">
          <button
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0}
            className="btn-secondary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed order-2 sm:order-1"
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Previous</span>
          </button>

          {/* Question Navigation with Pagination */}
          <div className="flex items-center space-x-2 overflow-x-auto max-w-full py-2 order-1 sm:order-2">
            {/* Previous Page Button */}
            {currentQuestionIndex > 4 && examQuestions.length > 10 && (
              <button
                onClick={() => setCurrentQuestionIndex(Math.max(0, currentQuestionIndex - 10))}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 text-gray-600 transition-colors"
                title="Previous 10 questions"
              >
                <ChevronsLeft className="h-4 w-4" />
              </button>
            )}
            
            {/* Question Numbers Window (max 10) */}
            <div className="flex space-x-2">
              {(() => {
                const windowSize = 10;
                const halfWindow = Math.floor(windowSize / 2);
                let start = Math.max(0, currentQuestionIndex - halfWindow);
                let end = Math.min(examQuestions.length, start + windowSize);
                
                // Adjust start if we're near the end
                if (end - start < windowSize && examQuestions.length >= windowSize) {
                  start = Math.max(0, end - windowSize);
                }
                
                return examQuestions.slice(start, end).map((_, idx) => {
                  const index = start + idx;
                  const questionId = currentQuestion.questionId;
                  const isAnswered = answers[questionId];
                  const isCurrent = index === currentQuestionIndex;
                  
                  return (
                    <button
                      key={index}
                      onClick={() => setCurrentQuestionIndex(index)}
                      className={`w-8 h-8 rounded-full text-sm font-medium transition-colors duration-200 ${
                        isCurrent
                          ? 'bg-blue-600 text-white'
                          : isAnswered
                          ? 'bg-green-100 text-green-800 border border-green-300'
                          : 'bg-gray-100 text-gray-800 border border-gray-300'
                      }`}
                    >
                      {index + 1}
                    </button>
                  );
                });
              })()}
            </div>
            
            {/* Next Page Button */}
            {currentQuestionIndex < examQuestions.length - 5 && examQuestions.length > 10 && (
              <button
                onClick={() => setCurrentQuestionIndex(Math.min(examQuestions.length - 1, currentQuestionIndex + 10))}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 text-gray-600 transition-colors"
                title="Next 10 questions"
              >
                <ChevronsRight className="h-4 w-4" />
              </button>
            )}
          </div>

          <button
            onClick={currentQuestionIndex === examQuestions.length - 1 ? confirmSubmitExam : handleNext}
            className="btn-primary flex items-center space-x-2 order-3"
          >
            <span className="hidden sm:inline">{currentQuestionIndex === examQuestions.length - 1 ? 'Submit' : 'Next'}</span>
            <span className="sm:hidden">{currentQuestionIndex === examQuestions.length - 1 ? 'Submit' : 'Next'}</span>
            {currentQuestionIndex === examQuestions.length - 1 ? null : <ChevronRight className="h-4 w-4" />}
          </button>
        </div>
      )}

        </div>

        {/* Right Column - Image Carousel */}
        {allQuestionImages.length > 0 && (
          <div className="w-full lg:w-64 lg:flex-shrink-0 order-1 lg:order-2">
            <div className="bg-white rounded-lg shadow p-4 sticky top-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                <span className="mr-2">Question Images</span>
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                  {allQuestionImages.length}
                </span>
              </h3>
              
              {/* Carousel Images */}
              <div className="space-y-3">
                {currentCarouselImages.map((item) => (
                  <button
                    key={item.questionIndex}
                    onClick={() => handleImageClick(item)}
                    className={`w-full group relative overflow-hidden rounded-lg border-2 transition-all ${
                      currentQuestionIndex === item.questionIndex
                        ? 'border-blue-500 ring-2 ring-blue-200'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <img
                      src={item.image}
                      alt={`Question ${item.questionNumber}`}
                      className="w-full h-32 object-cover"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                      <ZoomIn className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <div className="absolute bottom-1 left-1 bg-black/60 text-white text-xs px-2 py-0.5 rounded">
                      Q{item.questionNumber}
                    </div>
                  </button>
                ))}
              </div>

              {/* Carousel Navigation */}
              {totalCarouselPages > 1 && (
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200">
                  <button
                    onClick={handleCarouselPrev}
                    disabled={carouselPage === 0}
                    className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronUp className="h-4 w-4 text-gray-600" />
                  </button>
                  <span className="text-xs text-gray-600">
                    Page {carouselPage + 1} of {totalCarouselPages}
                  </span>
                  <button
                    onClick={handleCarouselNext}
                    disabled={carouselPage >= totalCarouselPages - 1}
                    className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronDown className="h-4 w-4 text-gray-600" />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Image Fullscreen Viewer */}
      {selectedImage && (
        <div
          ref={fullscreenRef}
          className="fixed inset-0 z-50 flex flex-col bg-black"
          style={{ cursor: isDragging ? 'grabbing' : 'default' }}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Top Bar */}
          <div className="flex items-center justify-between px-4 py-2 bg-black/70 backdrop-blur-sm border-b border-white/10 shrink-0">
            {/* Question info */}
            <span className="text-white text-sm font-medium">
              Question {selectedImage.questionNumber} of {examQuestions.length}
            </span>

            {/* Zoom Controls */}
            <div className="flex items-center space-x-1 bg-white/10 rounded-lg px-2 py-1">
              <button
                onClick={() => setZoomScale(prev => Math.max(1, prev - 0.25))}
                disabled={zoomScale <= 1}
                className="text-white hover:text-gray-300 p-1 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title="Zoom Out"
              >
                <ZoomOut className="h-5 w-5" />
              </button>
              <span className="text-white text-xs font-mono w-12 text-center select-none">
                {Math.round(zoomScale * 100)}%
              </span>
              <button
                onClick={() => setZoomScale(prev => Math.min(5, prev + 0.25))}
                disabled={zoomScale >= 5}
                className="text-white hover:text-gray-300 p-1 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title="Zoom In"
              >
                <ZoomIn className="h-5 w-5" />
              </button>
              <button
                onClick={() => { setZoomScale(1); setPan({ x: 0, y: 0 }); }}
                disabled={zoomScale === 1 && pan.x === 0 && pan.y === 0}
                className="text-white hover:text-gray-300 p-1 disabled:opacity-40 disabled:cursor-not-allowed transition-colors border-l border-white/20 pl-2 ml-1"
                title="Reset Zoom"
              >
                <RotateCcw className="h-4 w-4" />
              </button>
            </div>

            {/* Right controls */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => {
                  setCurrentQuestionIndex(selectedImage.questionIndex);
                  closeModal();
                }}
                className="text-white text-xs bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg transition-colors font-medium"
              >
                Go to Q{selectedImage.questionNumber}
              </button>
              <button
                onClick={closeModal}
                className="text-white hover:text-gray-300 p-2 transition-colors"
                title="Close"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Image Canvas — fills remaining space */}
          <div
            className="flex-1 overflow-hidden flex items-center justify-center"
            style={{ userSelect: 'none' }}
          >
            <img
              src={selectedImage.image}
              alt={`Question ${selectedImage.questionNumber}`}
              draggable="false"
              className={`max-w-full max-h-full object-contain select-none ${
                zoomScale > 1 ? (isDragging ? 'cursor-grabbing' : 'cursor-grab') : ''
              }`}
              style={{
                transform: `scale(${zoomScale}) translate(${Math.round(pan.x / zoomScale)}px, ${Math.round(pan.y / zoomScale)}px)`,
                transition: isDragging ? 'none' : 'transform 0.15s ease-out',
                imageRendering: zoomScale > 1 ? 'crisp-edges' : 'auto',
                WebkitImageRendering: zoomScale > 1 ? '-webkit-optimize-contrast' : 'auto',
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
              }}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
              onDoubleClick={handleDoubleClick}
            />
          </div>

          {/* Bottom hint */}
          <div className="text-center py-2 text-white/40 text-xs shrink-0">
            Double-click to toggle zoom · Drag to pan · Press Esc to close
          </div>
        </div>
      )}

      {/* Submit Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Submit Exam</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to submit your exam? You cannot change your answers after submission.
            </p>
            <div className="flex space-x-4">
              <button
                onClick={cancelSubmitExam}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={submitExam}
                className="btn-primary"
              >
                Submit Exam
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExamParticipation;
